import { Controller } from '@hotwired/stimulus';
import ApexCharts from 'apexcharts';

export default class extends Controller {
  static targets = ['revenueStreamsChart'];

  // Store chart instances for cleanup
  charts = new Map();

  connect() {
    // Clean up any existing chart instances first
    this.cleanupCharts();

    if (this.hasRevenueStreamsChartTarget) {
      this.initRevenueStreamsChart();
    }
  }

  // Clean up when navigating away or reconnecting
  disconnect() {
    this.cleanupCharts();
  }

  // Cleanup method to destroy chart instances
  cleanupCharts() {
    this.charts.forEach((chart, key) => {
      try {
        chart.destroy();
      } catch (e) {
        console.warn(`Failed to destroy chart: ${key}`, e);
      }
    });
    this.charts.clear();
  }

  initRevenueStreamsChart() {
    // Clear any existing content in the target to prevent duplication
    if (
      this.revenueStreamsChartTarget.hasChildNodes() &&
      this.revenueStreamsChartTarget.childNodes.length > 0
    ) {
      this.revenueStreamsChartTarget.innerHTML = '';
    }

    // Get the data from data attributes
    const sourcesJSON = this.revenueStreamsChartTarget.dataset.sources;
    const valuesJSON = this.revenueStreamsChartTarget.dataset.values;
    const totalRevenueJSON =
      this.revenueStreamsChartTarget.dataset.totalRevenue;
    const amountsJSON = this.revenueStreamsChartTarget.dataset.amounts;

    // Parse the JSON data
    const sources = JSON.parse(sourcesJSON);
    const percentageValues = JSON.parse(valuesJSON);
    const totalRevenue = parseFloat(JSON.parse(totalRevenueJSON || '0'));
    const amounts = amountsJSON
      ? JSON.parse(amountsJSON).map((amount) => parseFloat(amount) || 0)
      : [];

    if (
      !sources ||
      !sources.length ||
      !percentageValues ||
      !percentageValues.length
    ) {
      return;
    }

    // Convert to numeric array if needed
    const values = percentageValues.map((val) => parseFloat(val));

    // Find the maximum value to apply primary color
    const maxValue = Math.max(...values);
    const maxIndex = values.indexOf(maxValue);

    // Generate colors array with primary color for the largest slice
    const colors = values.map((_, i) => {
      if (i === maxIndex) {
        return '#818cf8'; // Primary color for the biggest slice
      } else {
        // Generate different colors for other slices
        return [
          '#4338ca',
          '#10B981',
          '#f97316',
          '#ec4899',
          '#f59e0b',
          '#0ea5e9',
        ][i % 6];
      }
    });

    // Create the pie chart configuration
    const options = {
      series: values,
      chart: {
        type: 'donut',
        height: '100%',
        animations: {
          enabled: true,
        },
      },
      labels: sources,
      colors: colors,
      legend: {
        show: false, // Hide the built-in legend
      },
      plotOptions: {
        pie: {
          donut: {
            size: '65%',
            labels: {
              show: true,
              name: {
                show: true,
                offsetY: -10,
                formatter: function () {
                  return 'Total';
                },
              },
              value: {
                show: true,
                fontSize: '22px',
                fontWeight: 'bold',
                color: '#000',
                formatter: function () {
                  return `$${totalRevenue.toFixed(2)}`;
                },
              },
              total: {
                show: true,
                showAlways: true,
                label: 'Total',
                fontSize: '16px',
                fontWeight: 'bold',
                formatter: function () {
                  return `$${totalRevenue.toFixed(2)}`;
                },
              },
            },
          },
        },
      },
      dataLabels: {
        enabled: false,
      },
      tooltip: {
        enabled: true,
        y: {
          formatter: function (value) {
            return value.toFixed(1) + '%';
          },
        },
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: {
              height: 200,
            },
          },
        },
      ],
      stroke: {
        width: 0,
      },
    };

    // Create and render the chart
    const chart = new ApexCharts(this.revenueStreamsChartTarget, options);
    chart.render();

    // Store chart instance for cleanup
    this.charts.set('revenueStreams', chart);
  }
}
